import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { SERVICES, type ServiceEntry } from "../data/services";
import {
  D3NetworkGraph,
  type NetworkGraphData,
  type NetworkNode,
} from "../components/charts/D3NetworkGraph";

const CATEGORY_COLOR: Record<ServiceEntry["category"], string> = {
  edge: "#60a5fa",
  ai: "#a78bfa",
  data: "#22d3ee",
  automation: "#10b981",
  observability: "#f59e0b",
  auth: "#fb7185",
  compute: "#e879f9",
};

const CATEGORY_LABEL: Record<ServiceEntry["category"], string> = {
  edge: "Edge",
  ai: "AI",
  data: "Data",
  automation: "Automation",
  observability: "Observability",
  auth: "Auth",
  compute: "Compute",
};

// Hand-curated relationships between services (cross-links).
const RELATIONS: Array<[string, string, number]> = [
  ["airouter", "litellm", 4],
  ["litellm", "aoai", 3],
  ["litellm", "ftmodel", 2],
  ["litellm", "ollama_i9", 2],
  ["litellm", "langfuse", 3],
  ["airouter", "openwebui", 1],
  ["openwebui", "litellm", 2],
  ["n8n", "supabase", 3],
  ["n8n", "litellm", 2],
  ["n8n", "atlassian", 2],
  ["dispatch", "ollama_i9", 3],
  ["admin", "supabase", 2],
  ["admin", "auth", 2],
  ["ops", "auth", 2],
  ["search", "supabase", 3],
  ["rcatalog", "cf", 1],
  ["wbcom", "cf", 1],
  ["admin", "cf", 1],
  ["monitor", "n8n", 2],
  ["monitor", "cf", 1],
  ["neo4j", "supabase", 1],
  ["metabase", "supabase", 2],
];

export default function Topology() {
  const [hovered, setHovered] = useState<NetworkNode | null>(null);
  const [selected, setSelected] = useState<NetworkNode | null>(null);

  const data: NetworkGraphData = useMemo(() => {
    const idSet = new Set(SERVICES.map((s) => s.id));
    return {
      nodes: SERVICES.map((s) => ({
        id: s.id,
        name: s.name,
        group: s.category,
        color: CATEGORY_COLOR[s.category],
        meta: {
          url: s.url,
          status: s.status,
          platform: s.platform,
          description: s.description,
          notes: s.notes,
          category: s.category,
        },
      })),
      links: RELATIONS.filter(([a, b]) => idSet.has(a) && idSet.has(b)).map(
        ([source, target, value]) => ({ source, target, value }),
      ),
    };
  }, []);

  const active = hovered ?? selected;
  const activeMeta = (active?.meta ?? {}) as Record<string, string | undefined>;

  return (
    <div className="space-y-6">
      <header>
        <div className="eyebrow">Layer · Architecture</div>
        <h1 className="text-3xl font-bold mt-1 tracking-tight">Service Topology</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Force-directed map of every deployed service across edge, AI, data, and automation
          layers. Hover a node for live metadata; drag to rearrange.
        </p>
      </header>

      {/* Legend */}
      <div className="glass p-4 ring-glass flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="eyebrow">Categories</span>
        {(Object.keys(CATEGORY_COLOR) as ServiceEntry["category"][]).map((cat) => (
          <span key={cat} className="flex items-center gap-2 text-xs text-slate-300">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: CATEGORY_COLOR[cat], boxShadow: `0 0 8px ${CATEGORY_COLOR[cat]}66` }}
            />
            <span className="font-mono uppercase tracking-wider text-[10px] text-slate-400">
              {CATEGORY_LABEL[cat]}
            </span>
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass ring-glass lg:col-span-2 p-4 overflow-hidden"
        >
          <D3NetworkGraph
            data={data}
            width={840}
            height={560}
            onNodeHover={setHovered}
            onNodeClick={setSelected}
          />
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass ring-glass p-5 space-y-4"
        >
          <div>
            <div className="eyebrow">Inspector</div>
            <h2 className="text-lg font-semibold mt-1">
              {active ? active.name : "Hover a node"}
            </h2>
            {active && (
              <div className="text-xs font-mono text-slate-500 mt-0.5">
                {String(activeMeta.platform ?? "")}
              </div>
            )}
          </div>

          {active ? (
            <div className="space-y-3 text-sm">
              <Field label="Status" value={String(activeMeta.status ?? "")} mono />
              <Field label="Category" value={String(activeMeta.category ?? "")} mono />
              <Field
                label="URL"
                value={String(activeMeta.url ?? "")}
                href={String(activeMeta.url ?? "")}
              />
              <div>
                <div className="eyebrow">Description</div>
                <p className="text-slate-300 mt-1 leading-relaxed">
                  {String(activeMeta.description ?? "")}
                </p>
              </div>
              {activeMeta.notes && (
                <div className="px-3 py-2 rounded-lg bg-accent-amber/[0.06] border border-accent-amber/[0.15] text-[12px] text-accent-amber/90">
                  {String(activeMeta.notes)}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500 leading-relaxed">
              Move the cursor over any node to inspect URL, platform, status, and notes. Click
              to pin a selection.
            </p>
          )}
        </motion.aside>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  href,
}: {
  label: string;
  value: string;
  mono?: boolean;
  href?: string;
}) {
  if (!value) return null;
  const content = (
    <span className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</span>
  );
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-1 text-xs flex items-center gap-1.5 break-all">
        {href && href.startsWith("http") ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-accent-blue hover:text-accent-cyan transition-colors flex items-center gap-1"
          >
            {value}
            <ExternalLink size={10} />
          </a>
        ) : (
          content
        )}
      </div>
    </div>
  );
}
